/**
 * Lobby — host or join a room, see connected players, ready up, start.
 *
 * The lobby owns the Network handshake. Once the host clicks Start, it:
 *   1. picks a deterministic seed,
 *   2. broadcasts a `start-match` event with the seed and the player roster,
 *   3. switches to GameContainer locally.
 *
 * Clients react to the same event by switching scenes too.
 */
import { Theme } from '../ui/Theme.js';
import { Button } from '../ui/Button.js';
import { Toast } from '../ui/Toast.js';
import { SceneBackground } from '../ui/SceneBackground.js';
import { PromptModal } from '../ui/PromptModal.js';
import { Random } from '../seed/Random.js';
import { platformStorage } from '../save/Storage.js';
import { MSG } from '../network/Sync.js';
import { onResize } from './ResponsiveScene.js';

export class Lobby extends Phaser.Scene {
  constructor() {
    super('Lobby');
  }

  create() {
    const reg = this.registry;
    this.manifest = reg.get('manifest');
    this.i18n = reg.get('i18n');
    this.net = reg.get('network');
    this.sync = reg.get('sync');
    this.latency = reg.get('latency');
    this.reconnect = reg.get('reconnect');
    this.toast = new Toast(this);

    this.cameras.main.setBackgroundColor(Theme.colors.bg);
    SceneBackground.fromRegistry(this);

    this._roster = []; // [{ id, name, ready }]
    this._mode = 'menu';
    this._roomCode = null;

    // `_mode` drives which layout is current. Re-run on resize so
    // orientation changes keep the lobby usable mid-handshake.
    onResize(this, () => this._rerender(), { debounceMs: 150 });

    // Wire network callbacks. Lobby is the only place that handles roster
    // events; once the match starts these are forwarded to GameContainer.
    this.net.onPlayerJoined = (id, name) => {
      this.sync.hostAddPlayer(id, name);
      this._roster.push({ id, name, ready: false });
      this.sync.hostBroadcastPlayers();
      this._render();
      this.toast.show((name || 'player') + ' joined', { kind: 'success' });
    };
    this.net.onPlayerLeft = (id) => {
      this.sync.hostRemovePlayer(id);
      this._roster = this._roster.filter((p) => p.id !== id);
      this.sync.hostBroadcastPlayers();
      this._render();
    };
    this.net.onError = (msg) => this.toast.show(msg || 'network error', { kind: 'danger' });
    this.sync.onEvent = (name, payload) => {
      if (name === 'players-changed') {
        this._roster = (payload || []).map((p) => ({ id: p.id, name: p.name, ready: false }));
        this._render();
      } else if (name === 'start-match') {
        this._enterGame(payload);
      }
    };
  }

  _renderMenu() {
    const cx = this.scale.width / 2;
    const cy = this.scale.height / 2;

    this._clear();
    this._objects.push(this.add.text(cx, cy - 120, this.i18n.t('lobby.title', 'Multiplayer'), {
      fontFamily: Theme.font.family,
      fontSize: Theme.font.sizes.xl,
      color: Theme.colors.text,
      fontStyle: 'bold',
      stroke: '#000000',
      strokeThickness: 5
    }).setOrigin(0.5));

    // Two main actions only — Host or Join. Players return to the
    // MainMenu via the browser back button or the in-game pause menu.
    const hostBtn = new Button({
      scene: this,
      x: cx, y: cy,
      label: this.i18n.t('lobby.host', 'Host a game'),
      onClick: () => this._host()
    });
    this._objects.push(hostBtn.container);

    const joinBtn = new Button({
      scene: this,
      x: cx, y: cy + 60,
      label: this.i18n.t('lobby.join', 'Join with code'),
      variant: 'secondary',
      onClick: () => this._promptJoin()
    });
    this._objects.push(joinBtn.container);
  }

  async _host() {
    const playerName = platformStorage.get('playerName', 'Player');
    try {
      const code = await this.net.hostGame(playerName);
      this.sync.hostAddPlayer('host', playerName);
      this._roster = [{ id: 'host', name: playerName, ready: true }];
      this._mode = 'host';
      this._roomCode = code;
      this._renderRoom(code);
    } catch (err) {
      this.toast.show('Host failed: ' + (err && err.message), { kind: 'danger' });
    }
  }

  /** Dispatch to the right render function based on the current lobby mode. */
  _rerender() {
    if (this._mode === 'menu') {
      this._renderMenu();
    } else if (this._roomCode) {
      this._renderRoom(this._roomCode);
    }
  }

  _promptJoin() {
    // Prefill with the last successful room code for convenience.
    const lastCode = platformStorage.get('lastRoomCode', '');
    const modal = new PromptModal({
      scene: this,
      title: this.i18n.t('lobby.joinTitle', 'Enter room code'),
      placeholder: '00000',
      initialValue: lastCode,
      inputMode: 'numeric',
      pattern: '[0-9]*',
      maxLength: 5,
      buttons: [
        {
          label: this.i18n.t('common.cancel', 'Cancel'),
          variant: 'secondary',
          onClick: (m) => m.close()
        },
        {
          label: this.i18n.t('lobby.join', 'Join'),
          variant: 'primary',
          isDefault: true,
          onClick: (m) => {
            const code = m.getValue();
            m.close();
            if (code) this._join(code);
          }
        }
      ],
      onSubmit: (code, m) => {
        m.close();
        if (code) this._join(code);
      }
    });
  }

  async _join(code) {
    const playerName = platformStorage.get('playerName', 'Player');
    try {
      this.toast.show('Joining ' + code + '…');
      await this.net.joinGame(code, playerName);
      platformStorage.set('lastRoomCode', code);
      this._mode = 'client';
      this._roomCode = code;
      // Arm latency probes and reconnection now that we have a live link.
      this.latency.start();
      this.reconnect.enable(code, playerName);
      this._renderRoom(code);
    } catch (err) {
      this.toast.show('Join failed: ' + (err && err.message), { kind: 'danger' });
    }
  }

  _renderRoom(roomCode) {
    this._clear();
    const W = this.scale.width;
    const H = this.scale.height;
    const cx = W / 2;

    this._objects.push(this.add.text(cx, H * 0.10, this.i18n.t('lobby.code', 'Room code'), {
      fontFamily: Theme.font.family,
      fontSize: Theme.font.sizes.md,
      color: Theme.colors.text,
      stroke: '#000000',
      strokeThickness: 3
    }).setOrigin(0.5));

    this._objects.push(this.add.text(cx, H * 0.18, roomCode, {
      fontFamily: Theme.font.family,
      fontSize: Theme.font.sizes.xxl,
      color: Theme.colors.accentHex,
      fontStyle: 'bold',
      stroke: '#000000',
      strokeThickness: 6
    }).setOrigin(0.5));

    this._rosterText = this.add.text(cx, H * 0.32, '', {
      fontFamily: Theme.font.family,
      fontSize: Theme.font.sizes.md,
      color: Theme.colors.text,
      align: 'center',
      stroke: '#000000',
      strokeThickness: 3
    }).setOrigin(0.5, 0);
    this._objects.push(this._rosterText);
    this._render();

    // Start / waiting text pinned to a relative position near the
    // bottom. `H - 80` looked fine on landscape but on portrait
    // phones (with browser chrome + safe-area insets) it could land
    // below the visible area — use a scale-relative anchor instead.
    if (this._mode === 'host') {
      const startBtn = new Button({
        scene: this,
        x: cx,
        y: H * 0.85,
        label: this.i18n.t('lobby.start', 'Start game'),
        onClick: () => this._startMatch()
      });
      this._objects.push(startBtn.container);
    } else {
      this._objects.push(this.add.text(cx, H * 0.85,
        this.i18n.t('lobby.waiting', 'Waiting for host…'), {
        fontFamily: Theme.font.family,
        fontSize: Theme.font.sizes.md,
        color: Theme.colors.text,
        stroke: '#000000',
        strokeThickness: 3
      }).setOrigin(0.5));
    }
  }

  _render() {
    if (!this._rosterText) return;
    const lines = this._roster.map((p) => '• ' + p.name);
    this._rosterText.setText(lines.join('\n'));
  }

  _startMatch() {
    if (this._roster.length < this.manifest.players.min) {
      this.toast.show('Need at least ' + this.manifest.players.min + ' players', { kind: 'danger' });
      return;
    }
    const seed = Random.newSeed();
    const payload = {
      seed,
      players: this._roster.map((p) => ({ id: p.id, name: p.name })),
      settings: {}
    };
    // Tell everyone to switch scene.
    this.net.broadcast({ t: MSG.EVENT, name: 'start-match', payload });
    this._enterGame(payload);
  }

  _enterGame(payload) {
    this.scene.start('GameContainer', payload);
  }

  _clear() {
    if (!this._objects) this._objects = [];
    for (const o of this._objects) {
      if (o && o.destroy) o.destroy();
    }
    this._objects = [];
  }
}
